import { useEffect } from "react";
import { Building2, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

function CompanyProfilePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-navy to-blue-900 py-16 sm:py-20 md:py-24 lg:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/10 p-4 rounded-full">
                <Building2 className="w-12 h-12 sm:w-16 sm:h-16 text-gold" />
              </div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6">
              Company Profile
            </h1>
            <p className="font-body text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto mb-8">
              Learn more about IMMACULATE's journey, values, and commitment to excellence in manpower solutions.
            </p>
            
            {/* Download Button */}
            <a 
              href="/assest/IHCL Dubai Profile.pdf" 
              download="IHCL-Dubai-Profile.pdf"
              className="inline-block"
            >
              <Button className="bg-gold hover:bg-gold/90 text-brand-navy font-semibold px-8 py-3 flex items-center gap-2">
                <Download className="w-5 h-5" />
                Download PDF
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* PDF Viewer Section */}
      <section className="bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              {/* PDF Viewer Header */}
              <div className="bg-brand-navy text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-gold" />
                  <h2 className="font-display text-lg sm:text-xl font-semibold">
                    IHCL Dubai Profile
                  </h2>
                </div>
                <a 
                  href="/assest/IHCL Dubai Profile.pdf" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-gold transition-colors"
                >
                  Open in new tab →
                </a>
              </div>
              
              {/* PDF Embed */}
              <div className="relative w-full" style={{ height: '80vh', minHeight: '600px' }}>
                <iframe
                  src="/assest/IHCL Dubai Profile.pdf"
                  className="w-full h-full"
                  title="IHCL Dubai Company Profile"
                  style={{ border: 'none' }}
                />
              </div>
            </div>

            {/* Alternative Download Section */}
            <div className="mt-8 text-center">
              <p className="text-text-dark mb-4">
                Having trouble viewing the PDF? You can download it directly.
              </p>
              <a 
                href="/assest/IHCL Dubai Profile.pdf" 
                download="IHCL-Dubai-Profile.pdf"
              >
                <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Download Company Profile
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Company Highlights Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-navy text-center mb-10 sm:mb-12">
              Why Choose IMMACULATE?
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              <div className="bg-gradient-to-br from-brand-navy to-blue-900 p-6 rounded-lg text-white">
                <h3 className="font-display text-xl font-bold mb-3 text-white">
                  Our Reach
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-gold mr-2">✓</span>
                    <span>Over multiple employees across multiple organizations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">✓</span>
                    <span>Presence in multiple locations across India</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-brand-navy to-blue-900 p-6 rounded-lg text-white">
                <h3 className="font-display text-xl font-bold mb-3 text-white">
                  Our Expertise
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-gold mr-2">✓</span>
                    <span>Industry-leading staffing solutions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">✓</span>
                    <span>Compliance with Indian labor laws</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-brand-navy to-blue-900 p-6 rounded-lg text-white">
                <h3 className="font-display text-xl font-bold mb-3 text-white">
                  Our Commitment
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-gold mr-2">✓</span>
                    <span>24/7 customer support</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">✓</span>
                    <span>Quality assurance in every placement</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-brand-navy to-blue-900 p-6 rounded-lg text-white">
                <h3 className="font-display text-xl font-bold mb-3 text-white">
                  Our Services
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-gold mr-2">✓</span>
                    <span>Comprehensive workforce solutions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-2">✓</span>
                    <span>Customized staffing packages</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CompanyProfilePage;

